/**
 * PATH: packages/ui/src/kanban/components/card-detail/LinkedEmailRow.tsx
 * FILE_ID: UI-KANBAN-CMP-LINKED-EMAIL-ROW-001
 * PURPOSE: Render ONE email linked to a card as the exact inbox <EmailRow>,
 *          collapsible in place into the FULL reader — the tenant-injected
 *          adapter.FullEmailReader (canonical Email Hub pane: tags, right-click,
 *          smart actions) when available, else the in-package <EmailThreadView>
 *          fallback. Rendered once per linked email by CardLinkedEmailSection
 *          so a card with many emails lists and expands them all.
 * WHY:     A card carries one `primary_email` + N `related_emails`. Until now the
 *          Card-tab email box showed only the primary, so a 2nd/3rd linked email
 *          was invisible (operator 2026-06-29: "the ability to add multiple
 *          emails in the kanban card is not working properly"). Each linked email
 *          now gets its own viewable, expandable row.
 * FLOW:
 *   ┌──────────┐    ┌──────────────────┐    ┌───────────────────────────┐
 *   │ emailId  │ →  │ adapter.getEmail │ →  │ collapsed <EmailRow>       │
 *   │          │    │ (preview)        │    │ ⇄ expanded <EmailThreadView>│
 *   └──────────┘    └──────────────────┘    └───────────────────────────┘
 * RELATED:
 *   - card-detail/CardLinkedEmailSection.tsx: maps linked ids → this row.
 *   - EmailThreadView.tsx: the expanded reader (shared with the Email tab).
 *   - components/email/EmailRow.tsx: the collapsed inbox row.
 *   - card-detail/emailRowMapping.ts: preview → EmailRow mapping helpers.
 */
import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { ChevronDown, ChevronUp, ExternalLink } from 'lucide-react'

import { EmailRow } from '../../../components/email/EmailRow'
import { EmailThreadView } from '../EmailThreadView'
import { NEUTRAL_CATEGORY, swallow, toEmailMessage } from './emailRowMapping'
import type { KanbanEmailAdapter } from '../../api'

export interface LinkedEmailRowProps {
  emailId: string
  adapter: KanbanEmailAdapter
  /** First (primary) email gets a "Primary" badge; the rest read "Linked". */
  isPrimary?: boolean
  /** Subject shown by the reader before its own fetch resolves. */
  fallbackSubject?: string
  /** Open THIS email in the drawer's full-height Email tab. When wired the
   *  footer shows an "Open in tab" action, so opening a non-primary email
   *  full lands on the right one (BUG-2026-06-29). */
  onOpenInTab?: () => void
  /** Owning card — threads into the injected full reader so sent copies /
   *  tag changes invalidate the right card caches. */
  cardId?: string | null
  /** Fired after a reader-side mutation (send/tag/archive) so the section
   *  can refresh the card + linked-email previews. */
  onChanged?: () => void
  /** Arc B — detach this email from the card (revokes the allocation,
   *  prunes related_emails, re-points primary server-side). Hidden when
   *  the host hasn't wired the unlink endpoint. */
  onDetach?: () => void
  detachBusy?: boolean
  /** Arc B — promote this (related) email to the card's primary. Only
   *  offered on non-primary rows when the host wired the endpoint. */
  onMakePrimary?: () => void
  makePrimaryBusy?: boolean
}

export function LinkedEmailRow({
  emailId,
  adapter,
  isPrimary = false,
  fallbackSubject,
  onOpenInTab,
  cardId,
  onChanged,
  onDetach,
  detachBusy,
  onMakePrimary,
  makePrimaryBusy,
}: LinkedEmailRowProps) {
  const [expanded, setExpanded] = useState(false)

  // Lightweight preview for the collapsed inbox row. Shares the same
  // ['kanban','card-linked-email', id] key the legacy single-primary path used,
  // so an already-warmed primary preview is reused with no extra fetch.
  const previewQ = useQuery({
    // cardId in the key so a reveal-scoped fetch never shares cache with a
    // bare getEmail (change #263).
    queryKey: ['kanban', 'card-linked-email', emailId, cardId ?? null],
    queryFn: () => adapter.getEmail(emailId, { cardId }),
    enabled: !!emailId,
    staleTime: 30_000,
  })

  return (
    <div className="overflow-hidden rounded-lg border border-neutral-200 dark:border-zinc-800">
      {expanded ? (
        // Expanded — morph the summary row into the FULL reader in a bounded,
        // scrollable box. READER CONVERGENCE ARC: prefer the tenant-injected
        // full Email Hub reader (tags, right-click, smart actions) — the
        // in-package EmailThreadView fork is only the read-only-bundle fallback.
        <div className="flex h-[min(70vh,640px)] min-h-[360px] flex-col">
          <div className="min-h-0 flex-1">
          {adapter.FullEmailReader ? (
            <adapter.FullEmailReader
              emailId={emailId}
              cardId={cardId}
              onChanged={onChanged}
              onClose={() => setExpanded(false)}
            />
          ) : (
            <EmailThreadView
              emailId={emailId}
              adapter={adapter}
              cardId={cardId}
              fallbackSubject={previewQ.data?.subject ?? fallbackSubject}
            />
          )}
          </div>
        </div>
      ) : previewQ.isLoading ? (
        <div className="h-[68px] animate-pulse bg-neutral-100 dark:bg-zinc-800/60" />
      ) : previewQ.isError ? (
        <div className="flex items-center justify-between gap-2 px-3 py-2 text-[11.5px] text-neutral-500 dark:text-zinc-400">
          <span>Couldn’t load this email.</span>
          <button
            type="button"
            onClick={() => void previewQ.refetch()}
            className="font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400"
          >
            Retry
          </button>
        </div>
      ) : previewQ.data ? (
        // Collapsed — the EXACT inbox row; click to expand into the reader.
        <EmailRow
          email={toEmailMessage(previewQ.data)}
          category={NEUTRAL_CATEGORY}
          isSelected={false}
          isChecked={false}
          selectable={false}
          onSelect={() => setExpanded(true)}
          onToggleSelect={swallow}
          onToggleStar={swallow}
        />
      ) : null}

      {/* Per-row footer — primary/linked badge + expand toggle. Each linked
          email owns its own expand state so several can be read independently. */}
      <div className="flex items-center justify-between border-t border-neutral-200 bg-neutral-50/60 px-2.5 py-1 dark:border-zinc-800 dark:bg-zinc-900/40">
        <span className="text-[10px] font-semibold uppercase tracking-wide text-neutral-400 dark:text-zinc-500">
          {isPrimary ? 'Primary' : 'Linked'}
        </span>
        <div className="flex items-center gap-2">
          {/* Arc B — link-lifecycle verbs: promote + detach. */}
          {!isPrimary && onMakePrimary && (
            <button
              type="button"
              onClick={onMakePrimary}
              disabled={makePrimaryBusy}
              title="Make this the card's primary email (the current primary stays linked)"
              className="text-[11px] font-medium text-neutral-500 hover:text-blue-600 disabled:opacity-50 dark:text-neutral-400 dark:hover:text-blue-300"
              data-testid={`linked-email-make-primary-${emailId}`}
            >
              {makePrimaryBusy ? 'Promoting…' : 'Make primary'}
            </button>
          )}
          {onDetach && (
            <button
              type="button"
              onClick={onDetach}
              disabled={detachBusy}
              title="Detach this email from the card (the email stays in your mailbox)"
              className="text-[11px] font-medium text-neutral-500 hover:text-rose-600 disabled:opacity-50 dark:text-neutral-400 dark:hover:text-rose-400"
              data-testid={`linked-email-detach-${emailId}`}
            >
              {detachBusy ? 'Detaching…' : 'Detach'}
            </button>
          )}
          {onOpenInTab && (
            <button
              type="button"
              onClick={onOpenInTab}
              title="Open this email in the full Email tab"
              className="inline-flex items-center gap-0.5 text-[11px] font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
            >
              <ExternalLink className="h-3 w-3" /> Open in tab
            </button>
          )}
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            aria-expanded={expanded}
            className="inline-flex items-center gap-0.5 text-[11px] font-medium text-neutral-500 hover:text-neutral-700 dark:text-neutral-400 dark:hover:text-neutral-200"
          >
            {expanded ? (
              <>
                <ChevronUp className="h-3 w-3" /> Collapse
              </>
            ) : (
              <>
                <ChevronDown className="h-3 w-3" /> Expand
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}
